import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { styled } from 'styletron-react';
import { HeadingMedium } from 'baseui/typography';
import { Button } from 'baseui/button';
import { Spinner } from 'baseui/spinner';
import { Select } from 'baseui/select';
import { Plus, Search } from 'lucide-react';
import { MantineDataTable } from '../components/MantineDataTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useServices, isBookingEngineSyncEnabled, isServiceActive, type Service, parseISODurationToMinutes } from '../hooks/useServices';
import { TableContent } from './LayoutPage';
import { useBusinessContext } from '../context/BusinessContext';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useBusinesses, type Business } from '../hooks/useBusinessAccessType';
import { Badge } from '../components/ui/Badge';
import { EngineSyncBadge } from '../components/ui/EngineConnect';
import { engineSyncState } from '../utils/engineMapping';
import {
  CREATE_SERVICE_TYPES, CREATE_SERVICE_SUBTYPES, type CreateServiceType,
} from './ServiceEditor/create/serviceCreateTaxonomy';
import { TableSkeleton } from '../components/TableSkeleton';
import { useFdCan } from '../hooks/useFdCan';

// SAM-198 — services live inside a business (Rodrigo, 2026-09-28): there is no cross-business
// "all services" view any more. A user who manages several businesses picks one here (defaults to
// the business in context); everyone else only ever sees their own.
type TypeFilter = 'all' | CreateServiceType | 'custom';

/** subtype -> [type, setup label], from the create-flow taxonomy so the list and the picker never disagree. */
const SETUP_BY_SUBTYPE: Record<string, { type: CreateServiceType; label: string }> = Object.fromEntries(
  (Object.keys(CREATE_SERVICE_SUBTYPES) as CreateServiceType[]).flatMap((t) =>
    CREATE_SERVICE_SUBTYPES[t].map((o) => [o.k, { type: t, label: o.n }])),
);
const TYPE_NAME: Record<string, string> = Object.fromEntries(CREATE_SERVICE_TYPES.map((t) => [t.k, t.n]));

function serviceTypeKey(s: Service): CreateServiceType | 'custom' {
  return SETUP_BY_SUBTYPE[s.subtype || '']?.type || 'custom';
}

const Container = styled('div', {
  backgroundColor: '#fff',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const Header = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px 0 24px',
});

const ToolbarRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 24px',
});

const SearchWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: '1',
  maxWidth: '280px',
  backgroundColor: '#EBEBEB',
  border: '1px solid #EBEBEB',
  borderRadius: '20px',
  padding: '7px 14px',
});

const SearchInputEl = styled('input', {
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '14px',
  color: '#374151',
  flex: 1,
  width: '100%',
  '::placeholder': { color: '#9CA3AF' },
});

const Main = styled('div', {
  flex: 1,
  overflow: 'auto',
  padding: '0 24px 24px',
});

const EmptyState = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  color: '#6B7280',
  fontSize: '14px',
});

const Passthrough: React.FC<{ children?: React.ReactNode }> = ({ children }) => <>{children}</>;

/**
 * `businessId` + `embedded` (SAM-198): the Services tab of the business page renders this
 * same list for one business, without the page chrome or the business picker.
 */
export const Services: React.FC<{ businessId?: string; embedded?: boolean }> = ({ businessId: forcedBusinessId, embedded = false }) => {
  const navigate = useNavigate();
  const { business } = useBusinessContext();
  const businessId = business?.id || null;
  const permissions = useUserPermissions();
  const canCreate = useFdCan('business', 'create');
  const canEdit = useFdCan('business', 'edit');

  const { data: businessesData } = useBusinesses();
  const businesses: Business[] = useMemo(() => {
    const raw = businessesData as any;
    if (Array.isArray(raw)) return raw;
    if (raw?.data && Array.isArray(raw.data)) return raw.data;
    return [];
  }, [businessesData]);

  // The business being viewed lives in the URL, so Back from the editor lands on the same one.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedBusinessFilter = searchParams.get('businessId');
  const setSelectedBusinessFilter = (id: string | null) => {
    setSearchParams(id ? { businessId: id } : {}, { replace: true });
  };
  // The admin and the tenant Front Desk see every business; the Front Desk has none of its own, so it starts
  // on the first one of the tenant.
  const seesAll = permissions.seesAllBusinesses;
  const canSwitchBusiness = seesAll && businesses.length > 1;

  const effectiveBusinessId = forcedBusinessId
    ?? (seesAll ? (selectedBusinessFilter || businessId || businesses[0]?.id || null) : businessId);
  const Shell = embedded ? Passthrough : TableContent;

  const { data: services, isLoading } = useServices(effectiveBusinessId);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: services?.length || 0 };
    (services || []).forEach((sv) => { const k = serviceTypeKey(sv); counts[k] = (counts[k] || 0) + 1; });
    return counts;
  }, [services]);

  const displayServices = useMemo(() => {
    if (!services) return [];
    const byType = typeFilter === 'all' ? services : services.filter((sv) => serviceTypeKey(sv) === typeFilter);
    if (!searchTerm.trim()) return byType;
    const q = searchTerm.toLowerCase();
    return byType.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.name_en?.toLowerCase().includes(q) ||
        s.name_es?.toLowerCase().includes(q) ||
        s.category?.toLowerCase().includes(q)
    );
  }, [services, searchTerm, typeFilter]);

  const engineSync = isBookingEngineSyncEnabled();
  const columns = useMemo<MRT_ColumnDef<Service>[]>(() => {
    const cols: MRT_ColumnDef<Service>[] = [
      {
        accessorKey: 'name_en',
        header: 'NAME',
        size: 220,
        Cell: ({ row }) => {
          const s = row.original;
          return <span>{s.name_en || s.name || s.name_es || '—'}</span>;
        },
      },
      {
        accessorKey: 'subtype',
        header: 'SETUP',
        size: 190,
        Cell: ({ row }) => {
          const setup = SETUP_BY_SUBTYPE[row.original.subtype || ''];
          if (!setup) return <span style={{ color: '#9CA3AF' }}>Custom</span>;
          return (
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
              <span style={{ fontSize: '11px', color: '#6B7280' }}>{TYPE_NAME[setup.type]}</span>
              <span>{setup.label}</span>
            </span>
          );
        },
      },
      {
        // SAM-533 — catalog_group is the nested read object for
        // service_group_id, added by the companion Lambda PR (#416); absent
        // entirely on a backend that predates it.
        accessorKey: 'catalog_group',
        header: 'GROUP',
        size: 140,
        Cell: ({ row }) => {
          const group = row.original.catalog_group;
          if (!group) return <span style={{ color: '#9CA3AF' }}>—</span>;
          return (
            <span
              style={{
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500,
                backgroundColor: '#F3F4F6',
                color: '#4B5563',
              }}
            >
              {group.name_en || group.name_es || '—'}
            </span>
          );
        },
      },
      {
        accessorKey: 'type',
        header: 'BOOKING TYPE',
        size: 120,
        Cell: ({ row }) => {
          const isDay = row.original.type === 'day';
          return (
            <span
              style={{
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500,
                backgroundColor: isDay ? '#EFF6FF' : '#F3F4F6',
                color: isDay ? '#2563EB' : '#4B5563',
              }}
            >
              {isDay ? 'Whole day' : 'Time slot'}
            </span>
          );
        },
      },
      {
        accessorKey: 'duration',
        header: 'DURATION',
        size: 110,
        Cell: ({ row }) => {
          const s = row.original;
          if (s.type === 'day') return <span style={{ color: '#9CA3AF' }}>—</span>;
          const dur = s.settings?.intervals?.duration || s.duration;
          if (!dur) return <span style={{ color: '#9CA3AF' }}>—</span>;
          const mins = typeof dur === 'string' && dur.startsWith('PT')
            ? parseISODurationToMinutes(dur)
            : Number(dur);
          return <span>{mins} min</span>;
        },
      },
      {
        accessorKey: 'status',
        header: 'STATUS',
        // SAM-1312 (U22): the engine state lives under the status (when the engine pilot is on) instead of a 7th column,
        // which did not fit at 1440. SAM-194 step 1.5 / SAM-198: off by default (VITE_BOOKING_ENGINE_SYNC); "synced" means
        // engine service + resource (utils/engineMapping); an unsynced row gets "Connect to engine", which does not open the row.
        size: engineSync ? 200 : 110,
        Cell: ({ row }) => {
          const isActive = isServiceActive(row.original);
          const status = (
            <Badge variant={isActive ? 'active' : 'inactive'}>
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          );
          if (!engineSync) return status;
          return (
            <div className="flex flex-col items-start gap-1">
              {status}
              <EngineSyncBadge state={engineSyncState(row.original)} serviceId={String(row.original.id)} canConnect={canEdit} />
            </div>
          );
        },
      },
    ];

    return cols;
  }, [canEdit, engineSync]);

  // SAM-198 — service configuration now lives on its own full page (too much information for a
  // drawer), not in ServiceDetailDrawer (removed). A row click / "Add Service" navigates there
  // instead of opening a side panel.
  const handleRowClick = (service: Service) => {
    // Carry the business being viewed: GET /services/description doesn't return business_id, so
    // without it the editor would fall back to the signed-in user's own business in its header.
    navigate(effectiveBusinessId ? `/services/${service.id}?businessId=${effectiveBusinessId}` : `/services/${service.id}`);
  };

  // Pre-scope the new service to the business being viewed, so the create flow never asks for it.
  const handleAddService = () => {
    navigate(effectiveBusinessId ? `/services/new?businessId=${effectiveBusinessId}` : '/services/new');
  };

  const businessFilterOptions = useMemo(
    () => [...businesses]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((b) => ({ label: b.name, id: b.id })),
    [businesses],
  );
  const typeChips: Array<{ k: TypeFilter; label: string }> = [
    { k: 'all', label: 'All' },
    ...CREATE_SERVICE_TYPES.map((t) => ({ k: t.k as TypeFilter, label: t.n })),
    ...(typeCounts.custom ? [{ k: 'custom' as TypeFilter, label: 'Custom' }] : []),
  ];

  if (isLoading) {
    return (
      <Shell>
        <Container>
          <Header>
            <HeadingMedium margin={0}>Services</HeadingMedium>
          </Header>
          {embedded ? (
            <TableSkeleton rows={5} bare label="Loading services…" />
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
              <Spinner />
            </div>
          )}
        </Container>
      </Shell>
    );
  }

  return (
    <Shell>
      <Container>
        <Header>
          <HeadingMedium margin={0}>{embedded ? '' : 'Services'}</HeadingMedium>
          {canCreate && <Button
            onClick={handleAddService}
            startEnhancer={<Plus size={16} />}
            overrides={{
              BaseButton: {
                style: {
                  backgroundColor: 'var(--tenant-primary, #111827)',
                  color: '#fff',
                  borderRadius: '8px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' },
                },
              },
            }}
          >
            New service
          </Button>}
        </Header>

        <ToolbarRow>
          <SearchWrapper>
            <Search size={16} color="#9CA3AF" />
            <SearchInputEl
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchWrapper>

          {canSwitchBusiness && !embedded && (
            <div style={{ width: '240px' }}>
              <Select
                options={businessFilterOptions}
                value={businessFilterOptions.filter((o) => o.id === effectiveBusinessId)}
                onChange={({ value }) => {
                  setSelectedBusinessFilter((value[0]?.id as string) || null);
                }}
                placeholder="Choose a business"
                size="compact"
                clearable={false}
                searchable
                overrides={{
                  Root: { style: { borderRadius: '20px' } },
                  ControlContainer: {
                    style: {
                      borderRadius: '20px',
                      backgroundColor: '#EBEBEB',
                      borderColor: '#EBEBEB',
                    },
                  },
                  Popover: {
                    props: {
                      overrides: {
                        Body: { style: { zIndex: 99999 } },
                      },
                    },
                  },
                  Dropdown: { style: { zIndex: 99999 } },
                }}
              />
            </div>
          )}

          <div role="group" aria-label="Filter by type" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {typeChips.map((c) => {
              const on = typeFilter === c.k;
              return (
                <button
                  key={c.k}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setTypeFilter(c.k)}
                  style={{
                    height: '32px', padding: '0 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
                    cursor: 'pointer',
                    border: `1px solid ${on ? 'var(--tenant-primary, #111827)' : '#E5E7EB'}`,
                    background: on ? 'var(--tenant-primary, #111827)' : '#fff',
                    color: on ? '#fff' : '#374151',
                  }}
                >
                  {c.label} <span style={{ opacity: 0.7 }}>{typeCounts[c.k] || 0}</span>
                </button>
              );
            })}
          </div>
        </ToolbarRow>

        <Main>
          {displayServices.length === 0 && !isLoading ? (
            <EmptyState>
              <p>No services found</p>
              {!searchTerm && canCreate && (
                <p style={{ fontSize: '13px', marginTop: '4px' }}>
                  Click "New service" to create your first service.
                </p>
              )}
            </EmptyState>
          ) : (
            <MantineDataTable
              columns={columns}
              data={displayServices}
              enablePagination={false}
              enableColumnFilters={false}
              enableSorting
              onRowClick={(row) => handleRowClick(row)}
            />
          )}
        </Main>
      </Container>
    </Shell>
  );
};
